import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, DatePicker, Form, Input, Select, Spin, Table, Tooltip } from "antd";
import moment, { Moment } from "moment";
import parse from "html-react-parser";
import "./PropertyModal.less";
import isEmpty from "lodash/isEmpty";
import { FormInstance } from "antd/es/form/Form";
import PropertyVersionsModal from "../PropertyVersionsModal/PropertyVersionsModal";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../lib/utils/formatDate";
import { expandedTableEyeIcon } from "../../../lib/icons";
import tickIcon from "../../../img/done.svg";
import deleteIcon from "../../../img/delete-round.svg";
import addProperty from "../../../img/addProperty.svg";
import propertiesMultiPlyStatusCodes from "../../../lib/const/propertiesMultiPlyStatusCodes";
import dataTypeIdesCodes from "../../../lib/const/dataTypeIdesCodes";
import { checkIsLoading } from "../../../lib/utils/checkLoadingStatus";
import { fetchDicType, fetchDicTypeSpecific } from "../../../store/slices/rtkDictionaries";
import { fetchPropertiesVersions } from "../../../store/slices/properties";
import { useAppSelector } from "../../..";
import {
  DictionaryDto,
  Property,
  PropertyTableData,
  PropertyType,
  TableColumn,
} from "../../../lib/interfaces";

interface ComponentProps {
  propertiesRequestBody: Partial<Property>;
  handleSetPropertiesRequestBody: (props: Partial<Property>) => void;
  resetPropertiesRequestBody: () => void;
  setFormPropertyEditStatus: React.Dispatch<React.SetStateAction<string>>;
  setEditingKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  editingKey: string | undefined;
  expandedRowKeys: string[];
  setExpandedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  isMakingNewProperty: boolean;
  isEditingPropertyToDate: boolean;
  isEditingForceProperty: boolean;
  isMakingNewPropertyVersion: boolean;
  dateObjectFromForCalendar?: string;
  dateObjectToForCalendar?: string;
  customPropertyTypes: PropertyType[];
  setCustomPropertyTypes: React.Dispatch<React.SetStateAction<PropertyType[]>>;
  setCloseDateInPast: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: () => void;
  checkedShowClosedProps: boolean;
  hasAccessToEdit: boolean;
  form: FormInstance<any>;
}

const PropertyModal = ({
  propertiesRequestBody,
  handleSetPropertiesRequestBody,
  resetPropertiesRequestBody,
  setFormPropertyEditStatus,
  setEditingKey,
  editingKey,
  expandedRowKeys,
  setExpandedRowKeys,
  isMakingNewProperty,
  isEditingPropertyToDate,
  isEditingForceProperty,
  isMakingNewPropertyVersion,
  dateObjectFromForCalendar,
  dateObjectToForCalendar,
  customPropertyTypes,
  setCustomPropertyTypes,
  setCloseDateInPast,
  handleSubmit,
  checkedShowClosedProps,
  hasAccessToEdit,
  form,
}: ComponentProps) => {
  const {
    dictionaries,
    dictionariesLoading,
    dicTypeWithParameter,
    dicTypeWithParameterLoading,
    dicTypeWithParameterSpecific,
    dicTypeWithParameterSpecificLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const { propertyTypesByObjectType } = useAppSelector((state) => state.propertyTypesAdmin);

  const {
    properties,
    propertiesLoading,
    propertiesVersionsLoading,
    createPropertyDataLoading,
    updatePropertyDataLoading,
    deletePropertiesLoading,
  } = useAppSelector((state) => state.properties);

  const dispatch = useDispatch();
  const dateFormat = displayFormat;

  const [tableData, setTableData] = useState<PropertyTableData[]>([]);
  // console.log({ tableData });
  const [dataTypeForCreateProperty, setDataTypeForCreateProperty] = useState<Partial<PropertyType>>(
    {}
  );
  const [customParameters, setCustomParameters] = useState<DictionaryDto[]>([]);

  const nowDate = moment();

  const isEditing = (rec: PropertyTableData) => rec.key === editingKey;

  const getPropValue = (props: Partial<Property>): string | number => {
    const { stringValue, numberValue, dateValue, dictionaryValue, propertyType } = props;
    const getResultValue = (value?: string | number): string | number => {
      if (value || value === 0 || value === "0") {
        return value;
      }
      return "";
    };
    if (!propertyType) {
      return "";
    }
    const dataTypeCode = propertyType.dataType;
    if (dataTypeCode === dataTypeIdesCodes.string) {
      return getResultValue(stringValue);
    }
    if (dataTypeCode === dataTypeIdesCodes.number) {
      return getResultValue(numberValue);
    }
    if (dataTypeCode === dataTypeIdesCodes.date) {
      return getResultValue(toDisplayFormat(dateValue));
    }
    if (dataTypeCode === dataTypeIdesCodes.dictionary) {
      if (propertyType.valDicTypeId && dictionaries) {
        const dicName = Object.entries(dictionaries).find(
          ([, dicValues]) => dicValues?.[0]?.dicTypeId === propertyType.valDicTypeId
        )?.[0];
        if (dicName) {
          const dictionaryValueName = dictionaries?.[dicName].find(
            ({ id }) => id === dictionaryValue
          )?.name;
          return getResultValue(dictionaryValueName);
        }
      }
    }
    return "";
  };

  // данные для отображения
  useEffect(() => {
    setTableData(
      properties.map(
        // TODO разобраться, почему типизация setTableData не срабатывает. Пришлось сюда прописать
        (item, index): PropertyTableData => {
          const propertyType =
            item.propertyType ||
            propertyTypesByObjectType.find(({ id }) => id === item.propertyTypeId);
          return {
            ...item,
            key: String(item.id),
            propertyType,
            pastDate: moment(item.toDate).isBefore(nowDate),
            lastVersion: index === 0 ? "last-version" : "",
          };
        }
      )
    );
  }, [properties, checkedShowClosedProps, propertyTypesByObjectType]);

  useEffect(() => {
    if (!isEmpty(propertyTypesByObjectType)) {
      setDataTypeForCreateProperty(
        propertyTypesByObjectType.find(
          (item) => item.id === propertiesRequestBody.propertyTypeId
        ) || {}
      );
    }
  }, [propertyTypesByObjectType, propertiesRequestBody.propertyTypeId]);

  useEffect(() => {
    if (dataTypeForCreateProperty.dataType === dataTypeIdesCodes.dictionary) {
      if (dataTypeForCreateProperty.valDicTypeAlias) {
        dispatch(fetchDicType(dataTypeForCreateProperty.valDicTypeAlias));
      }
    }
    // Загрузка словаря параметров (dicTypeWithParameterSpecific)
    if (dataTypeForCreateProperty.specificDicTypeAlias) {
      dispatch(fetchDicTypeSpecific(dataTypeForCreateProperty.specificDicTypeAlias));
    }
  }, [dataTypeForCreateProperty]);

  // Существующие параметры для каждого существующего свойства с multiTypeId === 4 (propertiesMultiPlyStatusCodes.many_single_by_dictionary)
  const getExistedManySingleParameters = () => {
    const manySinglePropsTypeIds: { [key: number]: number[] } = {};
    properties.forEach(({ propertyType, specificDicId }) => {
      if (
        propertyType?.multiplTypeId === propertiesMultiPlyStatusCodes.many_single_by_dictionary &&
        specificDicId
      ) {
        if (propertyType.id in manySinglePropsTypeIds) {
          manySinglePropsTypeIds[propertyType.id].push(specificDicId);
        } else {
          manySinglePropsTypeIds[propertyType.id] = [specificDicId];
        }
      }
    });
    return manySinglePropsTypeIds;
  };

  const getExcludeTypeIds = (): number[] => {
    const excludeTypeIds: number[] = [];
    const singleStatusCodes = [
      propertiesMultiPlyStatusCodes.single,
      propertiesMultiPlyStatusCodes.single_by_dictionary,
    ];
    properties.forEach(({ propertyType }) => {
      if (!propertyType) return;
      // Существующие свойства с multiplTypeId 1 и 2
      if (propertyType.multiplTypeId && singleStatusCodes.includes(propertyType.multiplTypeId)) {
        excludeTypeIds.push(propertyType.id);
      }
      // Существующие свойства с multiplTypeId 4, для которых существуют все возможные параметры,
      if (propertyType?.multiplTypeId === propertiesMultiPlyStatusCodes.many_single_by_dictionary) {
        const existedManySingleParameters = getExistedManySingleParameters();
        if (
          propertyType.specificDicTypeAlias &&
          existedManySingleParameters[propertyType.id].length ===
            dictionaries?.[propertyType.specificDicTypeAlias].length
        ) {
          excludeTypeIds.push(propertyType.id);
        }
      }
    });
    return excludeTypeIds;
  };

  // Наполнение словаря создаваемых свойств
  useEffect(() => {
    const filteredPropertyTypes = propertyTypesByObjectType.filter(
      (type) => !getExcludeTypeIds().includes(type.id)
    );
    setCustomPropertyTypes(filteredPropertyTypes);
  }, [properties, propertyTypesByObjectType]);

  // Наполнение словаря создаваемых параметров
  useEffect(() => {
    if (dicTypeWithParameterSpecific.length > 0) {
      const existedManySingleParameters = getExistedManySingleParameters();
      const filteredParameters = dicTypeWithParameterSpecific.filter((parameter) => {
        if (
          propertiesRequestBody?.propertyTypeId &&
          propertiesRequestBody.propertyTypeId in existedManySingleParameters
        ) {
          return !existedManySingleParameters[propertiesRequestBody.propertyTypeId].includes(
            parameter.id
          );
        }
        return true;
      });
      setCustomParameters(filteredParameters);
    }
  }, [properties, propertiesRequestBody?.propertyTypeId, dicTypeWithParameterSpecific]);

  const handleCreateProperty = (propertyEditStatus) => {
    setDataTypeForCreateProperty({});

    const dateObjectFromForCalendarParse = moment(
      dateObjectFromForCalendar ? toDisplayFormat(dateObjectFromForCalendar) : "",
      "DD.MM.YYYY"
    );

    const fromDateParse = () => {
      if (dateObjectFromForCalendarParse.isAfter(nowDate)) {
        return toHttpFormatStartOfDay(dateObjectFromForCalendarParse);
      }
      return toHttpFormatStartOfDay(nowDate);
    };

    handleSetPropertiesRequestBody({
      fromDate: fromDateParse(),
    });

    setEditingKey(() => `new${propertiesRequestBody.key}`);
    setFormPropertyEditStatus(propertyEditStatus);

    const newRecord: PropertyTableData = {
      ...propertiesRequestBody,
      key: `new${propertiesRequestBody.key}`,
      propertyTypeId: undefined,
      fromDate: fromDateParse(),
      stringValue: undefined,
      lastVersion: "create",
      numberValue: undefined,
      dateValue: undefined,
      dictionaryValue: undefined,
      specificDicId: undefined,
      dsc: "",
    };

    setTableData((prev) => [newRecord, ...prev]);
  };

  const renderAddIcon = () => {
    if (hasAccessToEdit && customPropertyTypes.length !== 0) {
      return (
        <div className="modal-create-property">
          <Tooltip title="Добавить свойство">
            <Button
              icon={<img src={addProperty} alt="add property" width="21px" />}
              type="text"
              htmlType="button"
              onClick={() => handleCreateProperty("createNewProperty")}
              disabled={
                isMakingNewProperty ||
                isEditingPropertyToDate ||
                isEditingForceProperty ||
                isMakingNewPropertyVersion
              }
            />
          </Tooltip>
        </div>
      );
    }
    return null;
  };

  const columnsModalProperty: TableColumn<PropertyTableData>[] = [
    {
      title: "Наименование",
      dataIndex: "propertyTypeId",
      key: "propertyTypeId",
      width: "22%",
      render: (x, record) => {
        const editable = isEditing(record);

        const onChangeType = (value, option) => {
          const { item } = option;
          handleSetPropertiesRequestBody({
            propertyTypeId: value,
            propertyTypeAlias: item.alias,
            objectTypeSysname: item.objectTypeSysname,
            objectTypeId: item.objectTypeId,
            specificDicId: undefined,
            stringValue: undefined,
            numberValue: undefined,
            dateValue: undefined,
            dictionaryValue: undefined,
          });
        };

        if (editable && isMakingNewProperty) {
          return (
            <Form.Item
              className="form-item-required__hidden-error__mb0"
              name="name"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder="Выберите свойство"
                onChange={onChangeType}
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {customPropertyTypes
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id} item={item}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          );
        }

        return <>{record.propertyType?.name}</>;
      },
    },
    {
      title: "Параметры",
      dataIndex: "specificDicId",
      key: "specificDicId",
      width: "20%",
      render: (x, record) => {
        const editable = isEditing(record);

        const onChangeDicType = (value) => {
          handleSetPropertiesRequestBody({
            specificDicId: value,
            stringValue: undefined,
            numberValue: undefined,
            dateValue: undefined,
            dictionaryValue: undefined,
          });
        };

        if (editable && isMakingNewProperty) {
          const isDisabled =
            !dataTypeForCreateProperty.specificDicTypeId || !propertiesRequestBody.propertyTypeId;
          const getPlaceholder = () => {
            if (propertiesRequestBody?.propertyTypeId) {
              if (dataTypeForCreateProperty.specificDicTypeId) {
                return "Выберите значение из словаря specific";
              }
              return "Нет доступных параметров";
            }
            return "Необходимо выбрать свойство";
          };
          return (
            <Form.Item
              className="form-item-required__hidden-error__mb0"
              name="specificDicId"
              rules={[
                {
                  required: !!dataTypeForCreateProperty.specificDicTypeId,
                },
              ]}
            >
              <Select
                placeholder={getPlaceholder()}
                style={{ width: "230px" }}
                onChange={onChangeDicType}
                disabled={isDisabled}
                loading={checkIsLoading(dicTypeWithParameterSpecificLoading)}
                showSearch
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {customParameters.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        }

        const getParameterName = () => {
          if (dictionaries && record.propertyType?.specificDicTypeId) {
            const findDicName = Object.entries(dictionaries).find(
              ([, dicValues]) =>
                dicValues?.[0]?.dicTypeId === record.propertyType?.specificDicTypeId
            )?.[0];

            return (
              dictionaries[findDicName || record.propertyType.specificDicTypeId].find(
                ({ id }) => id === record.specificDicId
              )?.name || ""
            );
          }
          return "";
        };

        return <>{getParameterName()}</>;
      },
    },
    {
      title: "Значение",
      dataIndex: "values",
      key: "values",
      width: "25%",
      render: (val, record) => {
        const editable = isEditing(record);

        const onChangeValueNum = (e) => {
          const { value } = e.target;
          const reg = /^-?\d*(\.\d*)?$/;
          // eslint-disable-next-line no-restricted-globals
          if ((!isNaN(value) && reg.test(value)) || value === "" || value === "-") {
            handleSetPropertiesRequestBody({
              numberValue: Number(value),
            });
          }
        };

        const onChangeValueString = (e) => {
          const { value } = e.target;
          handleSetPropertiesRequestBody({
            stringValue: value,
          });
        };

        const onChangeDate = (date: Moment | null) => {
          handleSetPropertiesRequestBody({
            dateValue: toHttpFormatStartOfDay(date),
          });
        };

        const onChangeDicType = (dictionaryValue) => {
          handleSetPropertiesRequestBody({
            dictionaryValue,
          });
        };
        const chooseValue = () => {
          const { dataType } = dataTypeForCreateProperty;

          if (dataType === dataTypeIdesCodes.number) {
            return (
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                name="numberValue"
                rules={[
                  {
                    required: true,
                  },
                  {
                    pattern: /^(|[0-9]*[.]?[0-9])+$/,
                  },
                ]}
              >
                <Input onChange={onChangeValueNum} placeholder="Введите числовое значение" />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.string) {
            return (
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                name="stringValue"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input onChange={onChangeValueString} placeholder="Введите строковое значение" />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.date) {
            return (
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                name="dateValue"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker format={dateFormat} onChange={onChangeDate} allowClear={false} />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.dictionary) {
            return (
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                name="dictionaryValue"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  placeholder={
                    dataTypeForCreateProperty.valDicTypeAlias
                      ? "Выберите значение из словаря"
                      : "Нет доступных значений"
                  }
                  style={{ width: "285px" }}
                  onChange={onChangeDicType}
                  disabled={!dataTypeForCreateProperty.valDicTypeAlias}
                  loading={checkIsLoading(dicTypeWithParameterLoading)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {dicTypeWithParameter.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return null;
        };

        return editable && isMakingNewProperty ? (
          <>
            {propertiesRequestBody.propertyTypeId ? (
              chooseValue()
            ) : (
              <Input placeholder="Необходимо выбрать свойство" disabled />
            )}
          </>
        ) : (
          <>{getPropValue(record)}</>
        );
      },
    },
    {
      title: "Дата с",
      dataIndex: "fromDate",
      key: "fromDate",
      width: "10%",
      render: (fromDate, record) => {
        const editable = isEditing(record);

        const dateObjectFromForCalendarParse = moment(
          dateObjectFromForCalendar ? toDisplayFormat(dateObjectFromForCalendar) : "",
          "DD.MM.YYYY"
        );

        // const dateObjectToForCalendarParse = moment(
        //   dateObjectToForCalendar ? toDisplayFormat(dateObjectToForCalendar) : "",
        //   "DD.MM.YYYY"
        // ).add(1, "day");

        const defaultValueNewProperty = () => {
          if (dateObjectFromForCalendarParse.isAfter(nowDate)) {
            return dateObjectFromForCalendarParse;
          }
          return nowDate;
        };

        const onChangeDate = (date) => {
          handleSetPropertiesRequestBody({
            fromDate: toHttpFormatStartOfDay(date),
          });
        };

        const disabledDatesForNewProperty = (current) =>
          current.isBefore(dateObjectFromForCalendarParse) ||
          // current.isAfter(dateObjectToForCalendarParse);
          current.isAfter(moment());

        return editable && isMakingNewProperty ? (
          <>
            <DatePicker
              format={dateFormat}
              style={{ width: "100px" }}
              onChange={onChangeDate}
              defaultValue={defaultValueNewProperty()}
              allowClear={false}
              disabledDate={disabledDatesForNewProperty}
            />
          </>
        ) : (
          <>{fromDate ? toDisplayFormat(fromDate) : ""}</>
        );
      },
    },
    {
      title: "Дата по",
      dataIndex: "toDate",
      key: "toDate",
      width: "10%",
      render: (toDate, record) => {
        const editable = isEditing(record);

        const dateObjectToForCalendarDisplayFormat = dateObjectToForCalendar
          ? toDisplayFormat(dateObjectToForCalendar)
          : "";
        const futureDate = `${
          dateObjectToForCalendar ? dateObjectToForCalendarDisplayFormat : "01.01.3000"
        }`;
        const fromDateValue = moment(record.fromDate, "DD.MM.YYYY HH:mm:ss");
        const toDateValue = moment(toDate, "DD.MM.YYYY HH:mm:ss");

        const disabledDate = (current) =>
          current.isBefore(fromDateValue) ||
          (dateObjectToForCalendar && current.isAfter(dateObjectToForCalendar));

        const onChangeDate = (date) => {
          handleSetPropertiesRequestBody({
            toDate: toHttpFormatEndOfDay(date),
          });
        };

        if (editable && isEditingPropertyToDate) {
          return (
            <DatePicker
              format={dateFormat}
              onChange={onChangeDate}
              style={{ width: "110px" }}
              defaultValue={toDateValue}
              disabledDate={disabledDate}
              allowClear={false}
            />
          );
        }

        return editable && isMakingNewProperty ? (
          <>{futureDate}</>
        ) : (
          <>{toDate ? toDisplayFormat(toDate) : ""}</>
        );
      },
    },
    {
      title: renderAddIcon(),
      colSpan: 2,
      dataIndex: "actions",
      key: "actions",
      render: (val, record, index) => {
        const editable = isEditing(record);
        const handleCancelCreate = () => {
          setTableData((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
          setEditingKey(undefined);
          setFormPropertyEditStatus("");
          resetPropertiesRequestBody();
        };

        if (hasAccessToEdit) {
          return (
            editable && (
              <>
                <Tooltip title="Подтвердить изменения">
                  {isMakingNewProperty && (
                    <Button
                      icon={<img src={tickIcon} alt="tick" width="16px" />}
                      className="icon"
                      type="text"
                      htmlType="submit"
                      onClick={handleSubmit}
                    />
                  )}
                </Tooltip>
                {isMakingNewProperty && (
                  <Tooltip title="Отменить действия">
                    <Button
                      icon={<img src={deleteIcon} width="16px" alt="delete" />}
                      type="text"
                      className="icon"
                      htmlType="button"
                      onClick={handleCancelCreate}
                    />
                  </Tooltip>
                )}
              </>
            )
          );
        }
        return null;
      },
    },
  ];

  const buildRowClassName = (record) => {
    const editable = isEditing(record);
    const editNew = record.key ? record.key.includes("new") : false;
    const activeClassName = "property-version-row";

    if (record.pastDate && !editable) return `${activeClassName} past-date`;
    if (record.pastDate && editable) return `${activeClassName} past-date active-row`;
    if (editable && !editNew) return `${activeClassName} active-row`;
    if (editable && editNew) return `${activeClassName} active-row new-prop`;
    return "";
  };

  return (
    <Form form={form}>
      <Table
        loading={checkIsLoading(
          dictionariesLoading,
          propertiesLoading,
          updatePropertyDataLoading,
          createPropertyDataLoading,
          deletePropertiesLoading
        )}
        columns={columnsModalProperty}
        dataSource={tableData}
        pagination={false}
        scroll={{ y: 400 }}
        expandedRowKeys={expandedRowKeys}
        rowClassName={buildRowClassName}
        expandedRowRender={() => {
          if (!checkIsLoading(propertiesVersionsLoading)) {
            return (
              <PropertyVersionsModal
                dateObjectToForCalendar={dateObjectToForCalendar}
                dicTypeWithParameter={dicTypeWithParameter}
                getPropValue={getPropValue}
                handleSetPropertiesRequestBody={handleSetPropertiesRequestBody}
                handleSubmit={handleSubmit}
                hasAccessToEdit={hasAccessToEdit}
                isEditing={isEditing}
                isEditingForceProperty={isEditingForceProperty}
                isEditingPropertyToDate={isEditingPropertyToDate}
                isMakingNewProperty={isMakingNewProperty}
                isMakingNewPropertyVersion={isMakingNewPropertyVersion}
                propertiesRequestBody={propertiesRequestBody}
                resetPropertiesRequestBody={resetPropertiesRequestBody}
                setCloseDateInPast={setCloseDateInPast}
                setEditingKey={setEditingKey}
                setFormPropertyEditStatus={setFormPropertyEditStatus}
              />
            );
          }
          return <Spin className="spin" />;
        }}
        expandIconColumnIndex={columnsModalProperty.length}
        expandIcon={({ expanded, record }) => {
          const handleClick = (e) => {
            e.stopPropagation();
            resetPropertiesRequestBody();
            if (!expanded && record.key && record.id) {
              setExpandedRowKeys([record.key]);
              setFormPropertyEditStatus("");
              setEditingKey(undefined);
              dispatch(fetchPropertiesVersions({ id: record.id }));
            } else {
              setExpandedRowKeys([]);
            }
          };
          return (
            record.lastVersion !== "create" && (
              <Button
                disabled={
                  isMakingNewProperty ||
                  isEditingPropertyToDate ||
                  isEditingForceProperty ||
                  isMakingNewPropertyVersion
                }
                onClick={handleClick}
                className="icon expanded-table-icon"
                type="text"
                htmlType="button"
              >
                {expandedTableEyeIcon(expanded ? "#0E5298" : "#86ACD2")}
              </Button>
            )
          );
        }}
      />
    </Form>
  );
};

export default PropertyModal;
